import type { ApiClient } from "@/modules/api/apiClient.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { queryKey as billingAccountListQueryKey } from "@/modules/billing/billingAccountQuery.ts";
import type { LocationInputs } from "@/modules/location/LocationField.tsx";
import { leaseUuidLink } from "@/modules/metal/links.ts";
import { getQueryKey as getMetalQueryKey } from "@/modules/metal/metalQuery.ts";
import { raiseRequestToast, type Toast } from "@/modules/notifications/toast.tsx";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { mutationOptions } from "@/utils/query/runMutation.ts";
import { queryOptions, type QueryClient, type QueryKey } from "@tanstack/react-query";
import type {
    MetalCommitment,
    MetalCommitmentCreateBody,
    MetalCommitmentPeriodEnum,
    MetalOsId,
} from "@warrenio/api-spec/spec.oats.gen";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { atomFamily } from "jotai-family";

type Response = MetalCommitment[];

function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["metal/commitment"] : ["metal/commitment", params.location];
}

interface Params {
    location: string;
}

function getQuery(client: ApiClient, params: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }): Promise<Response> => {
            const { location } = params;
            return getResponseData(
                await client.GET("/{location}/metal/commitments", {
                    signal,
                    params: { path: { location } },
                }),
            );
        },
    });
}

export const commitmentLocationQueryAtom = atomFamily((location: string) =>
    atomFromStandardQueryOptions(getQuery, { location }),
);

interface CommitmentBodyBase {
    period_hours: MetalCommitmentPeriodEnum;
    billing_account_id: number;
}

/** Reserve a bare metal server that does not exist yet - it is created together with the commitment. */
export function newLeaseCommitmentBody({
    period_hours,
    billing_account_id,
    requested_os_id,
    machine_spec_uuid,
    display_name,
}: CommitmentBodyBase & {
    requested_os_id: MetalOsId;
    machine_spec_uuid: string;
    display_name: string;
}): MetalCommitmentCreateBody {
    return {
        period_hours,
        billing_account_id,
        resource_request: {
            type: "new_metal_lease",
            requested_os_id,
            machine_spec_uuid,
            display_name,
        },
    };
}

/** Reserve an already existing bare metal server. */
export function existingLeaseCommitmentBody({
    period_hours,
    billing_account_id,
    metal_lease_uuid,
}: CommitmentBodyBase & { metal_lease_uuid: string }): MetalCommitmentCreateBody {
    return {
        period_hours,
        billing_account_id,
        resource_request: {
            type: "existing_metal_lease",
            metal_lease_uuid,
        },
    };
}

const toastOptions: Partial<Toast> = { icon: getResourceIconClass("bare_metal") };

interface MetalCommitmentCreateMutation extends LocationInputs {
    body: MetalCommitmentCreateBody;
}

export function commitmentCreateMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["bare_metal/create"],
        async mutationFn({ body, location }: MetalCommitmentCreateMutation) {
            return getResponseData(
                await api.POST("/{location}/metal/commitments", {
                    ...jsonEncodedBody,
                    params: { path: { location } },
                    body,
                }),
            );
        },
        async onSettled(res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Commitment created",
                successType: "success",
                error: "Error creating commitment",
                viewLink: res?.coverages[0]?.metal_lease_uuid
                    ? leaseUuidLink(res.coverages[0].metal_lease_uuid, location)
                    : undefined,
            });

            await queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) });
            await queryClient.invalidateQueries({ queryKey: getMetalQueryKey({ location }) });
            // Subtotal of the billing account must be updated (WAR-5839)
            discardPromise(queryClient.invalidateQueries({ queryKey: billingAccountListQueryKey }));
        },
    });
}
